import React, { useEffect, useMemo, useRef, useState } from 'react'

export default function MultiSelectFilterDropdown({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options...',
  emptyText = 'No records available',
  searchPlaceholder = 'Search...',
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const normalizedOptions = useMemo(() => {
    return (options || []).map((option) => {
      if (typeof option === 'object' && option !== null) {
        return {
          value: String(option.value ?? option.label ?? ''),
          label: String(option.label ?? option.value ?? '')
        }
      }

      return {
        value: String(option),
        label: String(option)
      }
    })
  }, [options])

  const normalizedSelectedValues = useMemo(
    () => (Array.isArray(selectedValues) ? selectedValues.map((value) => String(value)) : []),
    [selectedValues]
  )

  const filteredOptions = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase()
    if (!term) return normalizedOptions

    return normalizedOptions.filter((option) => option.label.toLowerCase().includes(term))
  }, [normalizedOptions, searchTerm])

  const selectedLabel = useMemo(() => {
    if (!selectedValues || selectedValues.length === 0) {
      return placeholder
    }

    const selectedSet = new Set(normalizedSelectedValues)
    const selectedLabels = normalizedOptions
      .filter((option) => selectedSet.has(option.value))
      .map((option) => option.label)

    if (selectedLabels.length <= 3) {
      return selectedLabels.join(', ')
    }

    return `${selectedLabels.length} selected`
  }, [placeholder, normalizedOptions, normalizedSelectedValues, selectedValues])

  const toggleValue = (value, checked) => {
    const normalizedValue = String(value)
    const current = Array.isArray(selectedValues) ? selectedValues.map((item) => String(item)) : []
    const next = checked
      ? [...current.filter((item) => item !== normalizedValue), normalizedValue]
      : current.filter((item) => item !== normalizedValue)
    onChange(next)
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}
      <button
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev)
          if (isOpen) {
            setSearchTerm('')
          }
        }}
        className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
      >
        <span className="truncate">{selectedLabel}</span>
        <span>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="p-2 space-y-1">
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-sm text-gray-500">{emptyText}</div>
            ) : (
              filteredOptions.map((option) => (
                <label key={option.value} className="flex items-center gap-3 p-2 hover:bg-blue-50 cursor-pointer rounded text-sm">
                  <input
                    type="checkbox"
                    checked={normalizedSelectedValues.includes(option.value)}
                    onChange={(event) => toggleValue(option.value, event.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}